/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Feature, Polygon, Point, Position } from 'geojson';
import { GoogleMapsShape, ETerraDrawMode } from '../../maps/models';

@Injectable({
  providedIn: 'root'
})
export class TerraDrawAdapterService {
  private readonly METERS_PER_KILOMETER = 1000;

  fromGeoJsonToGoogleMapsShape(geoJson: Feature): GoogleMapsShape | null {
    if (!this._isValidGeoJson(geoJson)) {
      return null;
    }

    const mode = geoJson.properties?.mode;

    switch (mode) {
      case ETerraDrawMode.Circle:
        return this._createCircleFromGeoJson(geoJson);
      case ETerraDrawMode.Rectangle:
        return this._createRectangleFromGeoJson(geoJson);
      default:
        return this._createPolygonFromGeoJson(geoJson);
    }
  }

  private _isValidGeoJson(geoJson: any): geoJson is Feature {
    return geoJson?.geometry && 'coordinates' in geoJson.geometry;
  }

  private _createCircleFromGeoJson(geoJson: Feature): google.maps.Circle {
    const coords = this._getPolygonCoordinates(geoJson);
    const center = this._calculateCenter(coords);
    const radius = (geoJson.properties?.radiusKilometers || 0) * this.METERS_PER_KILOMETER;

    return new google.maps.Circle({ center, radius });
  }

  private _createRectangleFromGeoJson(geoJson: Feature): google.maps.Rectangle {
    const coords = this._getPolygonCoordinates(geoJson);
    const bounds = new google.maps.LatLngBounds(
      this._createLatLng(coords[1]),
      this._createLatLng(coords[3])
    );

    return new google.maps.Rectangle({ bounds });
  }

  private _createPolygonFromGeoJson(geoJson: Feature): google.maps.Polygon | null {
    if (geoJson.geometry.type !== 'Polygon') {
      return null;
    }

    const coords = this._getPolygonCoordinates(geoJson);
    const path = coords.map(coord => this._createLatLng(coord));

    return new google.maps.Polygon({ paths: path });
  }

  private _getPolygonCoordinates(geoJson: Feature): Position[] {
    return (geoJson.geometry as Polygon).coordinates[0];
  }

  private _createLatLng(coord: Position): google.maps.LatLng {
    return new google.maps.LatLng(coord[1], coord[0]);
  }

  private _calculateCenter(coords: Position[]): google.maps.LatLng {
    const centerLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
    const centerLng = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;

    return new google.maps.LatLng(centerLat, centerLng);
  }

  fromGoogleMapsShapeToGeoJson(googleMapsShape: GoogleMapsShape): Feature | null {
    if (googleMapsShape instanceof google.maps.Circle) {
      return this._circleToGeoJson(googleMapsShape);
    }

    if (googleMapsShape instanceof google.maps.Rectangle) {
      return this._rectangleToGeoJson(googleMapsShape);
    }

    if (googleMapsShape instanceof google.maps.Polygon) {
      return this._polygonToGeoJson(googleMapsShape);
    }

    return null;
  }

  private _circleToGeoJson(circle: google.maps.Circle): Feature<Point> {
    const center = circle.getCenter();
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.lng(), center.lat()]
      },
      properties: {
        mode: ETerraDrawMode.Circle,
        radiusKilometers: circle.getRadius() / this.METERS_PER_KILOMETER
      }
    };
  }

  private _rectangleToGeoJson(rectangle: google.maps.Rectangle): Feature<Polygon> {
    const bounds = rectangle.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[
          [sw.lng(), ne.lat()],
          [ne.lng(), ne.lat()],
          [ne.lng(), sw.lat()],
          [sw.lng(), sw.lat()],
          [sw.lng(), ne.lat()]
        ]]
      },
      properties: {
        mode: ETerraDrawMode.Rectangle
      }
    };
  }

  private _polygonToGeoJson(polygon: google.maps.Polygon): Feature<Polygon> {
    const path = polygon.getPath();
    const coordinates = [path.getArray().map(point => [point.lng(), point.lat()])];

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates
      },
      properties: {}
    };
  }
}
